import { faObjectGroup } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import ConvertedUnits from 'src/components/ConvertedUnits';
import { getShortUuid } from 'src/utils/url';

const isWhite = color => ['white', '#fff', '#ffffff'].includes(color?.trim().toLowerCase());

const ScannedContainersList = ({
  containerUuids,
  namesByUuid,
  metaByUuid,
  isLoading,
  ariaLabel,
  emptyLabel,
  loadingLabel,
  unavailableLabel,
}) => {
  if (!containerUuids.length) {
    return emptyLabel ? <p className='scanned-containers-list__empty'>{emptyLabel}</p> : null;
  }

  return (
    <ol className='scanned-containers-list' aria-label={ariaLabel}>
      {containerUuids.map((uuid, index) => {
        const lotName = namesByUuid[uuid];
        const { quantity, units, color, materialName } = metaByUuid[uuid] || {};
        const statusLabel = isLoading ? loadingLabel : unavailableLabel;
        const hasAmount = quantity != null && units;

        return (
          <li key={uuid} className='scanned-containers-list__item'>
            <span
              className='badge-circle badge-circle--muted scanned-containers-list__index'
              aria-hidden='true'
            >
              {index + 1}
            </span>
            <div className='scanned-containers-list__body'>
              <div className='scanned-containers-list__header'>
                <span
                  className={`scanned-containers-list__name ${lotName ? '' : 'scanned-containers-list__name--muted'}`.trim()}
                >
                  {lotName || statusLabel}
                </span>
                {hasAmount && (
                  <ConvertedUnits
                    skipTooltip
                    displayZero
                    useUserSystemUnit
                    quantity={quantity}
                    units={units}
                    className='scanned-containers-list__amount'
                  />
                )}
              </div>
              <div className='scanned-containers-list__footer'>
                <span className='batch-split-uuid-badge scanned-containers-list__uuid'>
                  {getShortUuid(uuid)}
                </span>
                {(color || materialName) && (
                  <span className='scanned-containers-list__material'>
                    {materialName && (
                      <span className='scanned-containers-list__material-name'>{materialName}</span>
                    )}
                    {color && (
                      <span
                        className='scanned-containers-list__material-icon'
                        style={{
                          backgroundColor: color,
                          color: isWhite(color) ? '#111827' : '#fff',
                        }}
                        aria-label={`${materialName || 'Material'} color`}
                        role='img'
                      >
                        <FontAwesomeIcon icon={faObjectGroup} aria-hidden='true' />
                      </span>
                    )}
                  </span>
                )}
              </div>
            </div>
          </li>
        );
      })}
    </ol>
  );
};

ScannedContainersList.propTypes = {
  containerUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
  namesByUuid: PropTypes.objectOf(PropTypes.string),
  metaByUuid: PropTypes.objectOf(
    PropTypes.shape({
      quantity: PropTypes.number,
      units: PropTypes.string,
      color: PropTypes.string,
      materialName: PropTypes.string,
    })
  ),
  isLoading: PropTypes.bool,
  ariaLabel: PropTypes.string,
  emptyLabel: PropTypes.string,
  loadingLabel: PropTypes.string,
  unavailableLabel: PropTypes.string,
};

ScannedContainersList.defaultProps = {
  namesByUuid: {},
  metaByUuid: {},
  isLoading: false,
  ariaLabel: 'Containers',
  emptyLabel: '',
  loadingLabel: 'Loading material lot…',
  unavailableLabel: 'Material lot unavailable',
};

export default ScannedContainersList;
